/**
 * useShotImageDrop — shared per-shot "drop an image file → confirm → static image" flow
 * for the board tile, the timeline Viewer and the ShotBar thumbs.
 *
 * The accept filter matches the Import tab's looser rule (MIME prefix OR extension)
 * rather than ShotBar's original exact-type list. attachDroppedImage is the ONLY apply
 * path: it is readOnly-guarded, pushes the undo snapshot, honours the sceneReadyBuffer
 * flush-then-re-read ordering, sets hasStaticImage:true and clears historyId — a
 * hand-rolled store write here would be a fourth copy of the old 3-site
 * hasStaticImage bug (actions.ts:637/739/959).
 */
import { useCallback, useRef, useState } from 'react';
import i18next from 'i18next';
import { showToast } from '@/components/Toast.tsx';

const IMAGE_NAME_RE = /\.(png|jpe?g|webp|gif)$/i;

export function isDroppableImageFile(file: File): boolean {
  return file.type.startsWith('image/') || IMAGE_NAME_RE.test(file.name);
}

export interface ShotImageDropState {
  dropActive: boolean;
  pendingFile: File | null;
  /** The shot the file was dropped ON — captured at drop time, because the Viewer's
   *  resolved shot can change (playhead moves) between the drop and the Confirm tap. */
  pendingShotId: number | null;
  /** Spread unconditionally — empty when disabled (the locked/readOnly idiom).
   *  Carries the global file-drag governor's `data-file-drop-target` tag alongside the
   *  listeners rather than beside them at the call sites, so the tag cannot drift out of sync:
   *  tagged ⇔ the drop is live. Untagged elements are refused app-wide (dragDropGovernor.ts). */
  handlers: {
    onDragOver?: (e: React.DragEvent) => void;
    onDragLeave?: (e: React.DragEvent) => void;
    onDrop?: (e: React.DragEvent) => void;
    'data-file-drop-target'?: '';
  };
  confirm: () => void;
  cancel: () => void;
}

export function useShotImageDrop(shotId: number | null | undefined, disabled: boolean): ShotImageDropState {
  const [dropActive, setDropActive] = useState(false);
  const [pending, setPending] = useState<{ file: File; shotId: number } | null>(null);

  const onDragOver = useCallback((e: React.DragEvent) => {
    if (!e.dataTransfer.types.includes('Files')) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
    setDropActive(true);
  }, []);

  const onDragLeave = useCallback((e?: React.DragEvent) => {
    // dragleave bubbles from CHILD boundaries too — clearing unconditionally makes the
    // outline flicker on every internal crossing. Only clear when actually leaving.
    if (e && e.currentTarget.contains(e.relatedTarget as Node)) return;
    setDropActive(false);
  }, []);

  const onDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDropActive(false);
    const file = e.dataTransfer.files[0];
    if (!file || !isDroppableImageFile(file) || shotId == null) return;
    setPending({ file, shotId });
  }, [shotId]);

  // Ref (not state): two Confirm taps landing in one React batch would both read a
  // non-null `pending` from the same render closure — double apply, double undo snapshot.
  const confirmBusyRef = useRef(false);

  const confirm = useCallback(() => {
    if (confirmBusyRef.current) return;
    const p = pending;
    setPending(null);
    if (!p) return;
    confirmBusyRef.current = true;
    // Dynamic import keeps actions.ts out of the call sites' static graphs (the
    // ShotBar.tsx idiom). Success toast only when the write actually landed
    // (attachDroppedImage resolves false on the readOnly/no-project guards);
    // a FileReader failure surfaces as an error toast instead of a silent no-op.
    void import('@/store/actions.ts')
      .then(({ attachDroppedImage }) => attachDroppedImage(p.shotId, p.file))
      .then((applied) => {
        if (applied) showToast(i18next.t('common:shotImageApplied'), 'success');
      })
      .catch(() => showToast(i18next.t('common:shotImageApplyFailed'), 'error'))
      .finally(() => { confirmBusyRef.current = false; });
  }, [pending]);

  const cancel = useCallback(() => setPending(null), []);

  return {
    dropActive: disabled ? false : dropActive,
    pendingFile: pending?.file ?? null,
    pendingShotId: pending?.shotId ?? null,
    handlers: disabled ? {} : { onDragOver, onDragLeave, onDrop, 'data-file-drop-target': '' },
    confirm,
    cancel,
  };
}
